import { useState } from 'react';
import { RefreshCw, AlertCircle, Activity, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import FilterBar from './FilterBar';
import LogTable from './LogTable';
import RequestTimeline from './RequestTimeline';
import { useLogs, BOSH_FILTRELER } from '../hooks/useLogs';

function OzetKart({ baslik, sayi, renk, ikon: Ikon, alt }) {
  const renkler = {
    red:    { bg: 'bg-red-50',    border: 'border-red-200',   text: 'text-red-600',   icon: 'text-red-400'   },
    amber:  { bg: 'bg-amber-50',  border: 'border-amber-200', text: 'text-amber-600', icon: 'text-amber-400' },
    green:  { bg: 'bg-green-50',  border: 'border-green-200', text: 'text-green-600', icon: 'text-green-400' },
    blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',  text: 'text-blue-600',  icon: 'text-blue-400'  },
  };
  const r = renkler[renk] ?? renkler.blue;

  return (
    <div className={`${r.bg} ${r.border} border rounded-xl p-4 flex items-center gap-4`}>
      <div className={`w-10 h-10 rounded-lg bg-white flex items-center
                       justify-center shadow-sm border ${r.border}`}>
        <Ikon size={18} className={r.icon} />
      </div>
      <div>
        <p className='text-xs text-slate-500 font-medium'>{baslik}</p>
        <p className={`text-2xl font-bold ${r.text}`}>{sayi}</p>
        {alt && <p className='text-xs text-slate-400'>{alt}</p>}
      </div>
    </div>
  );
}

export default function OverviewPage() {
  const [filtreler, setFiltreler] = useState(BOSH_FILTRELER);
  const [secilenGroup, setSecilenGroup] = useState(null);

  const {
    data, allData, total, loading, error, lastFetch, refresh,
    sayfa, setSayfa, toplamSayfa, sayfaBoyutu,
  } = useLogs(filtreler);

  // Özet istatistikler — tüm filtrelenmiş veri üzerinden
  const hataliIstek       = allData.filter(d => d.renk === 'red').length;
  const validasyonluIstek = allData.filter(d => d.renk === 'yellow').length;
  const basariliIstek     = allData.filter(d => d.renk === 'green').length;

  function handleRowClick(group) {
    if (group.requestId) {
      setSecilenGroup(group);
    }
  }

  return (
    <div>
      {/* Başlık */}
      <div className='flex items-center justify-between mb-6'>
        <div>
          <h1 className='text-2xl font-bold text-slate-800'>Genel Görünüm</h1>
          <p className='text-sm text-slate-400 mt-0.5'>
            {lastFetch
              ? `Son güncelleme: ${lastFetch.toLocaleTimeString('tr-TR')}`
              : 'Veriler yükleniyor...'}
          </p>
        </div>
        <button onClick={refresh}
          className='flex items-center gap-2 px-4 py-2 bg-white border
                     border-gray-200 rounded-lg text-sm text-slate-600
                     hover:bg-gray-50 transition-colors shadow-sm'>
          <RefreshCw size={14}
            className={loading ? 'animate-spin text-blue-500' : 'text-gray-400'} />
          Yenile
        </button>
      </div>

      {/* Özet kartlar */}
      {!loading && (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
          <OzetKart
            baslik='Hatalı İstek'
            sayi={hataliIstek}
            renk='red'
            ikon={AlertCircle}
            alt={`${total} istekten`}
          />
          <OzetKart
            baslik='Validasyon Uyarısı'
            sayi={validasyonluIstek}
            renk='amber'
            ikon={Activity}
            alt={`${total} istekten`}
          />
          <OzetKart
            baslik='Başarılı İstek'
            sayi={basariliIstek}
            renk='green'
            ikon={CheckCircle}
            alt={`${total} istekten`}
          />
        </div>
      )}
      
      {/* Filtre */}
      <FilterBar
        filtreler={filtreler}
        onChange={setFiltreler}
        musteriSecimGoster={true}
      />

      {/* Sonuç sayısı + sayfalama bilgisi */}
      {!loading && !error && filtreler.musteri && (
        <div className='flex items-center justify-between mb-3'>
          <p className='text-sm text-slate-500'>
            <span className='font-semibold text-slate-700'>{total}</span> istek bulundu
            <span className='text-slate-400 ml-2'>
              (Sayfa {sayfa}/{toplamSayfa})
            </span>
          </p>
        </div>
      )}

      {/* Müşteri seçili değilse bilgilendirme */}
      {!filtreler.musteri ? (
        <div className='flex flex-col items-center justify-center py-20
                        bg-white rounded-xl border border-gray-200'>
          <div className='w-12 h-12 bg-blue-50 rounded-full flex items-center
                          justify-center mb-3'>
            <span className='text-2xl'>👥</span>
          </div>
          <p className='text-slate-500 font-medium'>Müşteri seçimi yapın</p>
          <p className='text-slate-400 text-sm mt-1'>
            Logları görüntülemek için yukarıdan bir müşteri seçin
          </p>
        </div>
      ) : error ? (
        <div className='bg-red-50 border border-red-200 rounded-xl p-4
                        text-sm text-red-600 flex items-center gap-2'>
          <AlertCircle size={16} />
          {error}
        </div>
      ) : loading ? (
        <div className='flex items-center justify-center py-20'>
          <div className='text-center'>
            <div className='w-8 h-8 border-2 border-blue-500 border-t-transparent
                            rounded-full animate-spin mx-auto mb-3' />
            <p className='text-sm text-slate-400'>Veriler yükleniyor...</p>
          </div>
        </div>
      ) : (
        <>
          <LogTable
            data={data}
            total={total}
            onRowClick={handleRowClick}
          />

          {/* Sayfalama */}
          {toplamSayfa > 1 && (
            <div className='flex items-center justify-between mt-4 px-1'>
              <p className='text-xs text-slate-400'>
                {(sayfa - 1) * sayfaBoyutu + 1}–{Math.min(sayfa * sayfaBoyutu, total)} / {total} istek
              </p>
              <div className='flex items-center gap-1'>
                <button
                  onClick={() => setSayfa(1)}
                  disabled={sayfa === 1}
                  className='px-2.5 py-1.5 text-xs rounded-lg border border-gray-200
                             hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors'>
                  İlk
                </button>
                <button
                  onClick={() => setSayfa(s => Math.max(1, s - 1))}
                  disabled={sayfa === 1}
                  className='p-1.5 rounded-lg border border-gray-200
                             hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors'>
                  <ChevronLeft size={14} />
                </button>

                {/* Sayfa numaraları */}
                {Array.from({ length: toplamSayfa }, (_, i) => i + 1)
                  .filter(s => s === 1 || s === toplamSayfa || Math.abs(s - sayfa) <= 2)
                  .reduce((acc, s, i, arr) => {
                    if (i > 0 && s - arr[i - 1] > 1) acc.push('...');
                    acc.push(s);
                    return acc;
                  }, [])
                  .map((s, i) =>
                    s === '...' ? (
                      <span key={`dot-${i}`} className='px-1 text-slate-400 text-xs'>…</span>
                    ) : (
                      <button key={s} onClick={() => setSayfa(s)}
                        className={`w-8 h-8 text-xs rounded-lg border transition-colors
                          ${s === sayfa
                            ? 'bg-blue-500 text-white border-blue-500 font-semibold'
                            : 'border-gray-200 hover:bg-gray-50 text-slate-600'}`}>
                        {s}
                      </button>
                    )
                  )}

                <button
                  onClick={() => setSayfa(s => Math.min(toplamSayfa, s + 1))}
                  disabled={sayfa === toplamSayfa}
                  className='p-1.5 rounded-lg border border-gray-200
                             hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors'>
                  <ChevronRight size={14} />
                </button>
                <button
                  onClick={() => setSayfa(toplamSayfa)}
                  disabled={sayfa === toplamSayfa}
                  className='px-2.5 py-1.5 text-xs rounded-lg border border-gray-200
                             hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors'>
                  Son
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Drawer */}
      {secilenGroup && (
        <>
          <div className='fixed inset-0 bg-black/20 z-40'
            onClick={() => setSecilenGroup(null)} />
          <RequestTimeline
            group={secilenGroup}
            onClose={() => setSecilenGroup(null)}
          />
        </>
      )}
    </div>
  );
}
